import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Trophy, Check, X, Loader2, Crown, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { play, soundOn, setSoundOn } from "@/lib/sounds";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/components/ChatMessage";
import { logWarn } from "@/lib/log";
import { Celebrate } from "@/components/Mascot";

function Ring({ remaining, total }) {
  const pct = Math.max(0, Math.min(1, (remaining ?? 0) / total));
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20" data-testid="game-countdown">
      <svg className="-rotate-90" viewBox="0 0 80 80"><circle cx="40" cy="40" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle cx="40" cy="40" r={r} stroke={pct > 0.3 ? "#2563FF" : "#FDE047"} strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 1s linear" }} /></svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-semibold">{Math.ceil(remaining ?? 0)}</span>
    </div>
  );
}

function Board({ rows, me }) {
  return (
    <ol className="space-y-1.5" data-testid="game-scoreboard">
      {rows.map((r, i) => (
        <li key={r.user_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-500 ${r.user_id === me ? "bg-sky-500/10 border border-sky-400/30" : "bg-white/[0.03]"}`}>
          <span className="w-5 text-neutral-500 text-sm">{i + 1}</span>
          {i === 0 && <Crown className="h-4 w-4 text-yellow-300" />}
          <span className="flex-1 text-sm truncate">{r.name}</span>
          <span className="font-mono text-sm">{r.score}</span>
        </li>
      ))}
    </ol>
  );
}

const KIND_LABEL = { live: "Live quiz race", truefalse: "True/false speed round", team: "Team battle", duel: "Flashcard duel", async: "48-hour challenge" };
const TEAM_CLS = { A: "text-sky-300 border-sky-400/30 bg-sky-500/10", B: "text-orange-200 border-orange-300/30 bg-orange-500/10" };

function TeamBoard({ teams, me }) {
  if (!teams) return null;
  return (
    <div className="space-y-2" data-testid="game-team-board">
      {teams.map((t, i) => (
        <div key={t.team} className={`rounded-xl border p-3 ${TEAM_CLS[t.team]}`} data-testid={`game-team-${t.team}`}>
          <div className="flex items-center justify-between font-medium">
            <span className="flex items-center gap-1.5">{i === 0 && t.score > 0 && <Crown className="h-4 w-4 text-yellow-300" />}Team {t.team}</span>
            <span className="font-mono">{t.score}</span>
          </div>
          <p className="text-xs mt-1 text-neutral-300 truncate">{t.members.map((m) => (m.user_id === me ? `${m.name} (you)` : m.name)).join(", ") || "No players yet"}</p>
        </div>
      ))}
    </div>
  );
}

function SoundToggle() {
  const [on, setOn] = useState(soundOn());
  const toggle = () => { setSoundOn(!on); setOn(!on); if (!on) play("correct"); };
  return (
    <button onClick={toggle} className="p-2 rounded-full border border-white/10 text-neutral-400 hover:text-white" title={on ? "Mute sounds" : "Turn sounds on"} data-testid="game-sound-toggle">
      {on ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
    </button>
  );
}

function didWin(g, uid) {
  if (g.teams) return g.teams[0].score > g.teams[1].score && g.teams[0].members.some((m) => m.user_id === uid);
  return g.leaderboard.length > 1 && g.leaderboard[0]?.user_id === uid;
}

function useGameSounds(g, uid) {
  const seen = useRef({});
  useEffect(() => {
    if (!g) return;
    const myWin = didWin(g, uid);
    const s = seen.current;
    const me = g.me;
    const isAsync = g.kind === "async";
    const idx = isAsync ? (me?.idx ?? 0) - 1 : g.current;
    const q = g.questions[idx];
    const mine = me?.answers?.[idx];
    if (q && mine && q.answer_index !== undefined && !s[`r${idx}`]) { s[`r${idx}`] = 1; play(mine.correct ? "correct" : "wrong"); }
    if (g.status === "active" && !isAsync && !s.start) { s.start = 1; play("start"); }
    const sec = Math.ceil(g.remaining ?? 99);
    if (g.phase === "question" && g.status === "active" && sec <= 3 && sec > 0 && s.tick !== `${g.current}-${sec}`) { s.tick = `${g.current}-${sec}`; play("tick"); }
    if (g.status === "finished" && myWin && !s.win) { s.win = 1; play("win"); }
  }, [g, uid]);
}

function Confetti() {
  const bits = Array.from({ length: 40 }, (_, i) => i);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-50">
      <style>{"@keyframes fall{to{transform:translateY(110vh) rotate(720deg);opacity:.6}}"}</style>
      {bits.map((i) => <span key={`confetti-${i}`} className="absolute top-[-20px] h-2.5 w-1.5 rounded-sm" style={{ left: `${(i * 37) % 100}%`, background: ["#2563FF", "#FFD54F", "#38E1FF", "#FFB6C8"][i % 4], animation: `fall ${2 + (i % 5) * 0.4}s ${(i % 7) * 0.15}s ease-in forwards` }} />)}
    </div>
  );
}

function RematchButton({ gameId }) {
  const navigate = useNavigate();
  const { refreshEnt, ent } = useAuth();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/games/${gameId}/rematch`);
      refreshEnt();
      if (!data.me) await http.post(`/games/${data.game_id}/join`);
      navigate(`/app/games/${data.game_id}`);
    } catch (e) { if (e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  if (!ent?.features.groups) return null;
  return (
    <button onClick={go} disabled={busy} data-testid="game-rematch-btn"
      className="px-6 py-2.5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50 flex items-center gap-2">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Rematch · same topic
    </button>
  );
}

function choiceClass(reveal, i, answer, picked) {
  if (reveal) {
    if (i === answer) return "border-emerald-400 bg-emerald-500/15";
    return i === picked ? "border-red-400 bg-red-500/10" : "border-white/10 opacity-60";
  }
  return i === picked ? "border-sky-400 bg-sky-500/15" : "border-white/10 hover:bg-white/5 hover:-translate-y-0.5";
}

function winnerText(g, winTeam, winner) {
  if (g.teams) return winTeam ? `Team ${winTeam} wins!` : "It's a tie!";
  return `${winner?.name} ${g.status === "finished" ? "wins!" : "leads so far"}`;
}

function Choices({ q, picked, onPick, disabled }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {q.choices.map((c, i) => {
        const reveal = q.answer_index !== undefined;
        const cls = choiceClass(reveal, i, q.answer_index, picked);
        const tf = q.choices.length === 2;
        return (
          <button key={`${q.question}-${c}`} disabled={disabled || reveal || picked !== undefined} onClick={() => onPick(i)} data-testid={`game-choice-${i}`}
            className={`text-left p-4 sm:p-5 rounded-2xl border-2 text-base font-medium transition-[transform,background-color,border-color] ${tf ? "text-center text-lg" : ""} ${cls}`}>
            <span className={`flex gap-2 ${tf ? "justify-center" : ""}`}>{!tf && <span className="text-neutral-500">{"ABCD"[i]}</span>}<span className="[&_p]:m-0"><Markdown>{c}</Markdown></span></span>
          </button>
        );
      })}
    </div>
  );
}

export default function GamePage() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get(`/games/${gameId}`).then((r) => setG(r.data)).catch((e) => logWarn("Couldn't refresh game", e)), [gameId]);
  useEffect(() => { load(); const t = setInterval(load, 1000); return () => clearInterval(t); }, [load]);
  useGameSounds(g, user?.user_id);
  const act = async (path, body) => {
    setBusy(true);
    try { const { data } = await http.post(`/games/${gameId}/${path}`, body); setG(data.game || data); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  if (!g) return <div className="p-10 text-neutral-500 text-sm">Loading game…</div>;
  const me = g.me;
  const isAsync = g.kind === "async";
  const idx = isAsync ? me?.idx ?? 0 : g.current;
  const q = g.questions[idx];
  const picked = me?.answers?.[idx]?.choice;
  const done = g.status === "finished" || (isAsync && me && me.idx >= g.questions.length);
  const winner = g.leaderboard[0];
  const myTeam = me?.team;
  const winTeam = g.teams && g.teams[0].score > g.teams[1].score ? g.teams[0].team : null;

  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      {done && (g.teams ? winTeam && winTeam === myTeam : winner?.user_id === user.user_id && g.leaderboard.length > 1) && <Confetti />}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid lg:grid-cols-[1fr_280px] gap-6 sm:gap-8">
        <div className="space-y-6 min-w-0">
          <div>
            <Link to={`/app/groups/${g.class_id}`} className="text-xs text-neutral-500 hover:text-white" data-testid="game-back-link">← Back to group</Link>
            <div className="flex items-start justify-between gap-3 mt-1">
              <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="game-title">{g.topic}</h1>
              <SoundToggle />
            </div>
            <p className="text-sm text-neutral-500">{KIND_LABEL[g.kind]} · hosted by {g.host_name}{myTeam && <> · you're on <span className={myTeam === "A" ? "text-sky-300" : "text-orange-200"}>Team {myTeam}</span></>}</p>
          </div>

          {g.status === "lobby" && (
            <div className="rounded-2xl border border-white/10 bg-[#121725] p-8 text-center" data-testid="game-lobby">
              <p className="text-lg">{g.leaderboard.length} in the lobby</p>
              <p className="text-sm text-neutral-500 mt-1">Need at least 2 players. Up to 30 can join.{g.kind === "team" && " New players are added to the smaller team."}</p>
              {g.is_host ? <button onClick={() => act("start")} disabled={busy || g.leaderboard.length < 2} className="mt-6 px-8 py-3 rounded-full bg-sky-400 text-white font-medium hover:bg-sky-500 disabled:opacity-40" data-testid="game-start-btn">Start race</button>
                : <p className="mt-6 text-sky-300">Waiting for the host to start…</p>}
            </div>
          )}
          {g.status === "invited" && <p className="text-neutral-400" data-testid="game-waiting-opponent">Waiting for your opponent to accept. Invites expire after 10 minutes.</p>}
          {g.status === "expired" && <p className="text-neutral-400">This game expired.</p>}
          {isAsync && g.status === "active" && me && !me.started_at && (
            <button onClick={() => act("begin")} className="px-8 py-3 rounded-full bg-sky-400 text-white font-medium" data-testid="game-begin-btn">Start my run</button>
          )}

          {!done && q && g.status === "active" && (!isAsync || me?.started_at) && (
            <div className="space-y-5 rise" key={idx} data-testid="game-question">
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-500">Question {idx + 1} of {g.questions.length}</p>
                {!isAsync && g.phase === "question" && <Ring remaining={g.remaining} total={g.time_limit} />}
                {!isAsync && g.phase === "reveal" && <span className="text-sm text-neutral-400">Next question…</span>}
              </div>
              <div className="text-xl"><Markdown>{q.question}</Markdown></div>
              <Choices q={q} picked={picked} disabled={busy} onPick={(c) => act("answer", { index: idx, choice: c })} />
              {q.explanation && <div className="text-sm text-neutral-400" data-testid="game-explanation"><Markdown>{q.explanation}</Markdown></div>}
            </div>
          )}

          {done && (
            <div className="space-y-5" data-testid="game-results">
              <div className="rounded-2xl border border-yellow-300/30 bg-yellow-300/5 p-6 flex items-center gap-4">
                <Trophy className="h-10 w-10 text-yellow-300" />
                <div><p className="font-display text-2xl font-semibold" data-testid="game-winner">{winnerText(g, winTeam, winner)}</p>
                  <p className="text-sm text-neutral-400">{isAsync && g.status !== "finished" ? "You've finished. The leaderboard stays open until the challenge closes." : "Here's how everyone did."}</p></div>
              </div>
              {(() => {
                const place = g.leaderboard.findIndex((p) => p.user_id === user.user_id);
                const won = g.teams ? winTeam && winTeam === myTeam : place === 0 && g.leaderboard.length > 1;
                if (g.status !== "finished" || !(won || (place > 0 && place < 3))) return null;
                return <Celebrate testid="game-celebrate" title={won ? "You won!" : `You placed #${place + 1}!`} text="Brilliant playing. Bryn is proud of you." />;
              })()}
              {g.status === "finished" && me && (
                <div className="flex items-center gap-3"><RematchButton gameId={g.game_id} /><span className="text-xs text-neutral-500">New questions, same topic. Joining stays free.</span></div>
              )}
              {g.questions.map((qq, i) => (
                <div key={`${g.game_id}-${i}`} className="rounded-xl border border-white/10 p-4">
                  <div className="flex gap-2 items-start">{me?.answers?.[i]?.correct ? <Check className="h-4 w-4 text-emerald-400 mt-1 shrink-0" /> : <X className="h-4 w-4 text-red-400 mt-1 shrink-0" />}<Markdown>{qq.question}</Markdown></div>
                  {qq.answer_index !== undefined && <div className="text-sm mt-2 [&_p]:my-1"><div className="text-emerald-300"><Markdown>{qq.choices[qq.answer_index]}</Markdown></div><div className="text-neutral-500"><Markdown>{qq.explanation}</Markdown></div></div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <aside className="space-y-3">
          <TeamBoard teams={g.teams} me={user.user_id} />
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-600">Scoreboard</p>
          <Board rows={g.leaderboard} me={user.user_id} />
          {busy && <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />}
        </aside>
      </div>
    </div>
  );
}
